/**
 * Hook para validación de permisos RBAC
 * Fecha: 16 Oct 2025
 */

import { useAuth } from '@/shared/hooks/useAuth';

/**
 * Hook personalizado para verificar permisos del usuario
 * Utiliza el sistema RBAC del backend
 */
export const usePermissions = () => {
  const { user, isLoading } = useAuth();

  /**
   * Verificar si el usuario tiene un permiso específico
   * @param permission - Nombre del permiso a verificar (ej: 'employees_create')
   * @returns true si tiene el permiso, false si no
   */
  const hasPermission = (permission: string): boolean => {
    if (!user || !user.rbac) {
      return false;
    }

    // Master tiene todos los permisos
    if (user.rbac.isMaster) {
      return true;
    }

    // Verificar en permisos efectivos (incluye permisos de roles + permisos directos)
    return user.rbac.effectivePermissions?.includes(permission) ?? false;
  };

  /**
   * Verificar si el usuario tiene AL MENOS UNO de los permisos proporcionados
   * @param permissions - Array de nombres de permisos
   * @returns true si tiene al menos uno de los permisos
   */
  const hasAnyPermission = (permissions: string[]): boolean => {
    if (!user || !user.rbac) {
      return false;
    }

    // Master tiene todos los permisos
    if (user.rbac.isMaster) {
      return true;
    }

    return permissions.some((permission) =>
      user.rbac?.effectivePermissions?.includes(permission)
    );
  };

  /**
   * Verificar si el usuario tiene TODOS los permisos proporcionados
   * @param permissions - Array de nombres de permisos
   * @returns true si tiene todos los permisos
   */
  const hasAllPermissions = (permissions: string[]): boolean => {
    if (!user || !user.rbac) {
      return false;
    }

    // Master tiene todos los permisos
    if (user.rbac.isMaster) {
      return true;
    }

    return permissions.every((permission) =>
      user.rbac?.effectivePermissions?.includes(permission)
    );
  };

  /**
   * Verificar si el usuario tiene un rol específico
   * @param roleKey - Key del rol a verificar (ej: 'MASTER')
   * @returns true si tiene el rol
   */
  const hasRole = (roleKey: string): boolean => {
    if (!user || !user.rbac) {
      return false;
    }

    return user.rbac.roles?.some((role) => role.key === roleKey) ?? false;
  };

  /**
   * Verificar si el usuario tiene AL MENOS UNO de los roles proporcionados
   * @param roleKeys - Array de keys de roles
   * @returns true si tiene al menos uno de los roles
   */
  const hasAnyRole = (roleKeys: string[]): boolean => {
    if (!user || !user.rbac) {
      return false;
    }

    return roleKeys.some((roleKey) => hasRole(roleKey));
  };

  /**
   * Verificar si el usuario es master (acceso total)
   * @returns true si es master
   */
  const isMaster = (): boolean => {
    return user?.rbac?.isMaster ?? false;
  };

  /**
   * Obtener todos los permisos efectivos del usuario
   * @returns Array de permisos efectivos
   */
  const getEffectivePermissions = (): string[] => {
    return user?.rbac?.effectivePermissions ?? [];
  };

  /**
   * Obtener todos los roles del usuario
   * @returns Array de roles con sus permisos
   */
  const getRoles = () => {
    return user?.rbac?.roles ?? [];
  };

  return {
    isLoading,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    hasAnyRole,
    isMaster,
    getEffectivePermissions,
    getRoles,
  };
};

/**
 * Constantes de permisos del módulo de empleados
 * Para facilitar el uso y evitar strings mágicos
 */
export const EMPLOYEE_PERMISSIONS = {
  // Empleados
  EMPLOYEES_CREATE: 'rh.empleados_expedientes.base.update',
  EMPLOYEES_READ: 'rh.empleados_expedientes.base.read',
  EMPLOYEES_UPDATE: 'rh.empleados_expedientes.base.update',
  EMPLOYEES_DELETE: 'rh.empleados_expedientes.base.update',
  EMPLOYEE_ENABLE_IMSS: 'rh.empleados_expedientes.base.update',
  EMPLOYEE_ENABLE_ERP: 'rh.empleados_expedientes.base.update',

  // Documentos de empleados
  EMPLOYEE_DOCUMENTS_CREATE: 'rh.empleados_expedientes.base.update',
  EMPLOYEE_DOCUMENTS_READ: 'rh.empleados_expedientes.base.read',
  EMPLOYEE_DOCUMENTS_UPDATE: 'rh.empleados_expedientes.base.update',
  EMPLOYEE_DOCUMENTS_DELETE: 'rh.empleados_expedientes.base.update',
  EMPLOYEE_DOCUMENTS_APPROVE: 'rh.empleados_expedientes.base.update',
  // Tipos de documentos
  DOCUMENT_TYPES_CREATE: 'rh.empleados_expedientes.base.update',
  DOCUMENT_TYPES_READ: 'rh.empleados_expedientes.base.read',
  DOCUMENT_TYPES_UPDATE: 'rh.empleados_expedientes.base.update',
  DOCUMENT_TYPES_DELETE: 'rh.empleados_expedientes.base.update',

  // Contratos
  CONTRACTS_CREATE: 'rh.empleados_expedientes.base.update',
  CONTRACTS_READ: 'rh.empleados_expedientes.base.read',
  CONTRACTS_UPDATE: 'rh.empleados_expedientes.base.update',
  CONTRACTS_DELETE: 'rh.empleados_expedientes.base.update',
  CONTRACTS_GENERATE: 'rh.empleados_expedientes.base.update',
  CONTRACTS_SIGN: 'rh.empleados_expedientes.base.update',

  // Contratación
  CANDIDATES_HIRE: 'rh.reclutamiento_candidatos.especial.change_status',
} as const;

/**
 * Constantes de permisos del módulo de almacén.
 * @deprecated Importa desde '@/constants/permissions' → ALMACEN
 */
export { ALMACEN_PERMISSIONS } from '@/constants/permissions';
